import { environment } from '@/config/environment'

interface PerformanceMetrics {
  pageLoadTime: number
  domContentLoaded: number
  firstContentfulPaint?: number
  largestContentfulPaint?: number
  cumulativeLayoutShift?: number
}

interface UserInteraction {
  type: 'click' | 'scroll' | 'input' | 'navigation'
  target: string
  timestamp: number
  duration?: number
}

class PerformanceMonitor {
  private metrics: PerformanceMetrics = {
    pageLoadTime: 0,
    domContentLoaded: 0,
  }

  private interactions: UserInteraction[] = []

  constructor() {
    this.initializeObservers()
  }

  private initializeObservers() {
    // Track page load performance
    this.trackPageLoad()
    
    // Track user interactions
    this.trackUserInteractions()
    
    // Track Core Web Vitals
    this.trackCoreWebVitals()
  }

  private trackPageLoad() {
    window.addEventListener('load', () => {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
      
      this.metrics.pageLoadTime = navigation.loadEventEnd - navigation.loadEventStart
      this.metrics.domContentLoaded = navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart
      
      console.log('Page load metrics:', this.metrics)
      
      // Send to analytics in production
      if (environment.ENVIRONMENT === 'production') {
        this.sendMetrics('page_load', this.metrics)
      }
    })
  }

  private trackUserInteractions() {
    // Track clicks
    document.addEventListener('click', (event) => {
      const target = (event.target as HTMLElement).tagName.toLowerCase()
      this.interactions.push({
        type: 'click',
        target,
        timestamp: Date.now(),
      })
    })

    // Track scroll events (throttled)
    let scrollTimeout: NodeJS.Timeout
    document.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(() => {
        this.interactions.push({
          type: 'scroll',
          target: 'document',
          timestamp: Date.now(),
        })
      }, 100)
    })

    // Track input events
    document.addEventListener('input', (event) => {
      const target = (event.target as HTMLInputElement).tagName.toLowerCase()
      this.interactions.push({
        type: 'input',
        target,
        timestamp: Date.now(),
      })
    })
  }

  private trackCoreWebVitals() {
    // First Contentful Paint
    new PerformanceObserver((list) => {
      const entries = list.getEntries()
      const fcp = entries[entries.length - 1]
      if (fcp) {
        this.metrics.firstContentfulPaint = fcp.startTime
      }
    }).observe({ entryTypes: ['paint'] })

    // Largest Contentful Paint
    new PerformanceObserver((list) => {
      const entries = list.getEntries()
      const lcp = entries[entries.length - 1]
      if (lcp) {
        this.metrics.largestContentfulPaint = lcp.startTime
      }
    }).observe({ entryTypes: ['largest-contentful-paint'] })

    // Cumulative Layout Shift
    new PerformanceObserver((list) => {
      let cls = 0
      for (const entry of list.getEntries()) {
        const layoutShiftEntry = entry as any
        if (!layoutShiftEntry.hadRecentInput) {
          cls += layoutShiftEntry.value || 0
        }
      }
      this.metrics.cumulativeLayoutShift = cls
    }).observe({ entryTypes: ['layout-shift'] })
  }

  private sendMetrics(event: string, data: any) {
    // Send to analytics service
    if (environment.FEATURES.ANALYTICS_ENABLED) {
      // Example: Mixpanel, Google Analytics, etc.
      console.log('Sending metrics:', event, data)
    }
  }

  // Public methods
  measurePerformance = (name: string, fn: () => void) => {
    const start = performance.now()
    fn()
    const end = performance.now()
    const duration = end - start
    
    console.log(`${name} took ${duration}ms`)
    
    if (environment.ENVIRONMENT === 'production') {
      this.sendMetrics('performance_measure', { name, duration })
    }
    
    return duration
  }

  measureAsyncPerformance = async (name: string, fn: () => Promise<any>) => {
    const start = performance.now()
    const result = await fn()
    const end = performance.now()
    const duration = end - start
    
    console.log(`${name} took ${duration}ms`)
    
    if (environment.ENVIRONMENT === 'production') {
      this.sendMetrics('async_performance_measure', { name, duration })
    }
    
    return { result, duration }
  }

  getMetrics = () => this.metrics

  getInteractions = () => this.interactions

  clearInteractions = () => {
    this.interactions = []
  }
}

export const performanceMonitor = new PerformanceMonitor()

// Utility functions
export const measurePerformance = (name: string, fn: () => void) => {
  return performanceMonitor.measurePerformance(name, fn)
}

export const measureAsyncPerformance = async (name: string, fn: () => Promise<any>) => {
  return performanceMonitor.measureAsyncPerformance(name, fn)
}

export const trackPageLoad = () => {
  // This is handled automatically by the PerformanceMonitor constructor
  console.log('Page load tracking initialized')
} 